import React, { useCallback, useContext } from 'react';
import type { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import Heading from '@atlaskit/heading/heading';
import CheckCircleIcon from '@atlaskit/icon/core/status-success';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import EditIcon from '@atlaskit/icon/core/edit';
import EyeOpenStrikethroughIcon from '@atlaskit/icon/core/eye-open-strikethrough';
import { token } from '@atlaskit/tokens';
import Lozenge from '@atlaskit/lozenge/lozenge';

import { viewRolePermissionsMessages } from './viewRolePermissionsMessages.ts';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { CloseButton } from '@atlaskit/modal-dialog/close-button';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import { Grid } from '@atlaskit/primitives/grid';
import Inline from '@atlaskit/primitives/inline';
import { xcss } from '@atlaskit/primitives/xcss';
import Stack from '@atlaskit/primitives/stack';
import Text from '@atlaskit/primitives/text';
import Flex from '@atlaskit/primitives/components/flex';
import { media } from '@atlaskit/primitives/responsive/media';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { ADMIN_SITE_SPACE_PERMISSIONS } from '@confluence/named-routes/entry-points/routes.ts';

import { ModalHeaderActionsMenu } from '../ModalHeaderActionsMenu.tsx';
import type { SpacePermission, SpaceRole } from '../../model/space-roles-types.ts';
import {
	getNormalizedPermissionItems,
	buildPermissionDictionary,
} from '../normalizePermissionItems.ts';

const i18n = defineMessages({
	title: {
		id: 'space-roles.view-role-permissions-modal.title',
		defaultMessage: 'Role permissions',
		description: 'Title for the view role permissions modal',
	},
	doneButtonText: {
		id: 'space-roles.view-role-permissions-modal.done-button-text',
		defaultMessage: 'Done',
		description: 'Button label for closing the view role permissions modal dialog',
	},
	permissionEnabled: {
		id: 'space-roles.view-role-permissions-modal.permission-enabled',
		defaultMessage: 'Enabled',
		description: 'Status text indicating a specific permission is currently enabled for a role',
	},
	permissionDisabled: {
		id: 'space-roles.view-role-permissions-modal.permission-disabled',
		defaultMessage: 'Disabled',
		description: 'Status text indicating a specific permission is currently disabled for a role',
	},
	editButtonLabel: {
		id: 'space-roles.view-role-permissions-modal.edit-button-label',
		defaultMessage: 'Edit role permissions',
		description: 'Button label to navigate to the role editing screen to modify permissions',
	},
});

// -- Styles --
const detailViewStyles = xcss({
	marginLeft: 'space.600',
});

const rolePermissionDetailContainerStyle = xcss({
	marginTop: 'space.400',
	rowGap: 'space.400',
	columnGap: 'space.1000',
	[media.above.sm]: { gridTemplateColumns: 'repeat(2, 1fr)' },
});

const contentStyles = xcss({
	paddingTop: 'space.200',
	paddingBottom: 'space.200',
});

const checkboxGroupStyles = xcss({
	gap: 'space.100',
	justifyContent: 'start',
});

const modalHeaderStyle = xcss({
	marginBottom: 'space.150',
	justifyContent: 'flex-end',
	gap: 'space.100',
	width: '100%',
});

const headingInlineStyle = xcss({
	marginBlock: 'space.negative.150',
});

const modalHeaderSpacing = xcss({
	height: '100%',
	alignItems: 'center',
	width: '100%',
});

type ExtraPermissionsContent = Record<string, React.ReactNode>;

export interface ViewRolePermissionsWrapperProps {
	systemRoles: SpaceRole[];
	customRoles?: SpaceRole[];
	allPermissions?: SpacePermission[];
	activeRoleId: string;
	setActiveRoleId: (roleId: string) => void;
	analyticsSource: string;
	onClose: () => void;
	onClickEdit?: (roleId?: string) => void;
	children: React.ReactNode;
	title?: string;
	footer?: React.ReactNode;
	extraPermissionContent?: ExtraPermissionsContent;
}

export const ViewRolePermissionsWrapper: FC<ViewRolePermissionsWrapperProps> = ({
	systemRoles,
	customRoles,
	allPermissions,
	onClose,
	onClickEdit,
	activeRoleId,
	analyticsSource,
	title,
	children,
	footer,
	extraPermissionContent,
}) => {
	const { formatMessage } = useIntl();
	const { Modal } = useChoreographerAssets();
	const { isSiteAdmin } = useContext(SPAViewContext);

	const defaultOnClickEdit = useCallback(() => {
		onClose();
		window.location.href = ADMIN_SITE_SPACE_PERMISSIONS.toUrl({
			tab: 'roles',
		});
	}, [onClose]);

	const handleClickEdit = onClickEdit || defaultOnClickEdit;

	const activeRole = [...systemRoles, ...(customRoles || [])].find(
		(role) => role.id === activeRoleId,
	);

	// Right side detail view
	// Build a dictionary for the active role to look up if a permission is enabled or not
	const activeRolePermissionDict = buildPermissionDictionary(activeRole?.permissions || []);
	// Get all permissions and group them by group name
	const sortedPermissionGroupDict = getNormalizedPermissionItems(undefined, allPermissions || []);

	const permissionListComps = Object.values(sortedPermissionGroupDict).map((group) => {
		return (
			<Stack space="space.150" key={group.group.name} xcss={checkboxGroupStyles}>
				<Heading size="xsmall">{group.group.name}</Heading>
				{group.group.checkboxes.map((item) => {
					return (
						<Inline space="space.100" alignBlock="center" key={item.id}>
							{activeRolePermissionDict[item.id] ? (
								<CheckCircleIcon
									label={formatMessage(i18n.permissionEnabled)}
									color={token('color.icon.success')}
									spacing="none"
								/>
							) : (
								<CrossCircleIcon
									label={formatMessage(i18n.permissionDisabled)}
									color={token('color.icon.disabled')}
									spacing="none"
								/>
							)}
							<Text>{item.displayName}</Text>
							{extraPermissionContent?.[item.id] && extraPermissionContent[item.id]}
						</Inline>
					);
				})}
			</Stack>
		);
	});

	return (
		<ModalTransition>
			<Modal width={1000} onClose={onClose}>
				{/* eslint-disable-next-line @atlaskit/design-system/use-modal-title */}
				<ModalHeader>
					<Flex xcss={modalHeaderSpacing}>
						<Heading size="medium">{title || formatMessage(i18n.title)}</Heading>
					</Flex>
					<Flex xcss={modalHeaderStyle}>
						<ModalHeaderActionsMenu source={analyticsSource} />
						<CloseButton onClick={onClose} />
					</Flex>
				</ModalHeader>
				<ModalBody>
					<Inline xcss={contentStyles}>
						{children}
						<Box xcss={detailViewStyles}>
							<Stack space="space.100">
								<Inline space="space.100" alignBlock="center">
									<Heading size="medium">{activeRole?.name || ''}</Heading>
									{activeRole?.isDisabled && (
										<Lozenge appearance="danger">
											<Inline space="space.050" alignBlock="center">
												<EyeOpenStrikethroughIcon label="" color="currentColor" />
												{formatMessage(viewRolePermissionsMessages.roleDisabledLabel)}
											</Inline>
										</Lozenge>
									)}
									{(onClickEdit || isSiteAdmin) &&
										activeRole &&
										customRoles?.some((role) => role.id === activeRole.id) && (
											<Box xcss={headingInlineStyle}>
												<IconButton
													appearance="subtle"
													icon={EditIcon}
													label={formatMessage(i18n.editButtonLabel)}
													onClick={() => handleClickEdit(activeRole.id)}
													testId="edit-role-permissions-button"
												/>
											</Box>
										)}
								</Inline>
								<Text>{activeRole?.description || ''}</Text>
							</Stack>
							<Grid xcss={rolePermissionDetailContainerStyle}>{permissionListComps}</Grid>
						</Box>
					</Inline>
				</ModalBody>
				{footer || (
					<ModalFooter>
						<Box>
							<Button appearance="primary" onClick={onClose}>
								{formatMessage(i18n.doneButtonText)}
							</Button>
						</Box>
					</ModalFooter>
				)}
			</Modal>
		</ModalTransition>
	);
};
